import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  businessHoursTableQuery,
  easeetaskShellQuery,
  organisationTableQuery,
  projectNotificationsQuery,
  rebuildProjectNotifications,
  removeOrganisation,
  saveBusinessHours,
  saveProjectNotifications,
  type EaseetaskTab,
} from '../../api/settingsEaseetask'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { AddMemberModal, OrganisationModal } from './EaseetaskForms'

/**
 * Settings -> Easeetask, adica modulul care in meniu se cheama "Dailee".
 *
 * Element cu element fata de machetele din `views/version-3/easeetask_settings/`
 * (`index.php` cu bara din stanga, `projects/index.php`,
 * `organisations/organisations.php`, `organisations/index.php`), si coloana cu
 * coloana fata de `OrganisationsListModel` si `OrganisationsModel` din
 * `public/js/settings/easeetask.js`.
 *
 * O SINGURA PAGINA, CU TREI FILE. In portal, bara din stanga aducea prin ajax
 * cate un fragment in acelasi invelis, fara sa schimbe adresa; aici filele sunt
 * stare de pagina, ceea ce e acelasi lucru vazut de om.
 *
 * CE NU S-A MUTAT, SI DE CE
 * -------------------------
 * Scrierile raman in portalul vechi: crearea si stergerea organizatiilor,
 * schimbarea rolurilor, invitatiile pe email si editarea orelor in celula.
 * Sapte din ele sunt aparate azi de un drept `.read` si nu intreaba deloc daca
 * organizatia e a celui care apasa (`deleteOrganisation`, `updateOrganisation`,
 * `addMember`, `getActionTabs`, `updateUserBusinessHours`). Nu se muta asa cum
 * sunt, si nici nu se repara in treacat intr-o banda de mutare: fiecare are
 * nevoie de dreptul ei si de o poarta pe organizatie, si aia e o hotarare, nu
 * o transcriere.
 *
 * Coloana "Actions" din lista de organizatii nu se deseneaza aici: singurul ei
 * buton era Delete, adica exact una din scrierile care n-au venit. Un buton
 * care nu face nimic e mai rau decat lipsa lui.
 */

const ORGANISATIONS_TABLE = 'easeetask-organisations'

const HOURS_TABLE = 'easeetask-business-hours'

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** `<i class="fas fa-minus font-10">` - ce scria modelul vechi in locul unei valori lipsa. */
function textOrDash(value: unknown): string {
  const text = String(value ?? '')
  return text === '' ? '<i class="fas fa-minus font-10"></i>' : esc(text)
}

/** `OrganisationsListModel` din easeetask.js, fara coloana Actions (vezi capul fisierului). */
export function organisationColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: 'Organisation name',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) =>
        esc(String(row.name ?? '')) +
        ' ' +
        (Number(row.main) === 1
          ? "<span style='color:#6495ED; font-weight: bold;'>Main</span>"
          : ''),
    },
    { index: 1, title: 'Email', orderable: true, visible: true, className: 'a-left', html: (row) => textOrDash(row.email) },
    { index: 2, title: 'Phone', orderable: true, visible: true, className: 'a-left', html: (row) => textOrDash(row.phone) },
    {
      index: 3,
      title: 'Private',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => (Number(row.private) === 1 ? 'yes' : 'no'),
    },
    { index: 4, title: 'Website', orderable: true, visible: true, className: 'a-left', html: (row) => textOrDash(row.website) },
    {
      index: 5,
      title: 'Role',
      orderable: false,
      visible: true,
      className: 'a-left',
      html: (row) => (String(row.role) === 'ROLE_ADMIN' ? 'ADMIN' : 'MEMBER'),
    },
    {
      /* Butoanele randului desfasurat (`getActionTabs`: Edit, Add members,
         Delete) - numai pentru ADMIN, cum le arata macheta veche. */
      index: 6,
      title: 'Actions',
      orderable: false,
      visible: true,
      className: 'a-right',
      html: (row) =>
        String(row.role) === 'ROLE_ADMIN'
          ? `<button type="button" class="btn btn-xs btn-primary edit-organisation" data-id="${Number(row.id)}"><i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button> ` +
            `<button type="button" class="btn btn-xs btn-success add-members" data-id="${Number(row.id)}"><i class="fas fa-user-plus" aria-hidden="true"></i> Add members</button> ` +
            `<button type="button" class="btn btn-xs btn-danger delete-organisation" data-id="${Number(row.id)}"><i class="fas fa-trash" aria-hidden="true"></i> Delete</button>`
          : '',
    },
  ]
}

/**
 * Coloana SQL a fiecarui indice. Harta din model e `[0 => id, 1 => name,
 * 2 => email, 3 => phone, 4 => private, 5 => website, 6 => role]`, cu indicele
 * 0 luat de coloana de desfasurare - care aici nu exista, fiindca panoul pe
 * care il deschidea era chiar cel cu butoanele de scriere.
 */
export const ORGANISATION_ORDER_NAMES = ['name', 'email', 'phone', 'private', 'website', 'role']

/** `OrganisationsModel` din easeetask.js: trei coloane, exact acelea. */
export function businessHoursColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: 'User',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(`${String(row.userName ?? '')} ${String(row.userFirstname ?? '')}`),
    },
    /* Editarea in celula (`editable` din easeetask.js -> `updateUserBusinessHours/{user}`):
       o casuta de ora in loc de textul gol al celulei. */
    {
      index: 1,
      title: 'Start',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => `<input type="time" class="form-control input-sm business-hour" data-user="${Number(row.userId ?? row.user_id ?? 0)}" data-column="Start" value="${esc(String(row.start ?? '').slice(0, 5))}">`,
    },
    {
      index: 2,
      title: 'End',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => `<input type="time" class="form-control input-sm business-hour" data-user="${Number(row.userId ?? row.user_id ?? 0)}" data-column="End" value="${esc(String(row.end ?? '').slice(0, 5))}">`,
    },
  ]
}

export const HOURS_ORDER_NAMES = ['userName', 'start_hour', 'end_hour']

function ProjectNotificationsPanel() {
  const q = useQuery(projectNotificationsQuery)
  const client = useQueryClient()
  const [busy, setBusy] = useState(false)

  if (q.error instanceof ApiError && q.error.status === 403) return <Forbidden />
  if (!q.data) return <Spinner />

  const flip = (active: boolean) => {
    setBusy(true)
    void saveProjectNotifications(active)
      .then(() => {
        toast(active ? 'Notifications enabled.' : 'Notifications disabled.', 'Success')
        return client.invalidateQueries({ queryKey: ['settings', 'easeetask'] })
      })
      .catch((err: unknown) => toast(err instanceof ApiError && err.message ? err.message : 'Something went wrong.', 'Error', 'error'))
      .finally(() => setBusy(false))
  }
  const rebuild = () => {
    setBusy(true)
    void rebuildProjectNotifications()
      .then((r) => toast(r.ok ? 'Cron configuration refreshed.' : r.message || 'The cron server did not answer.', 'Refresh cron', r.ok ? 'success' : 'error'))
      .catch((err: unknown) => toast(err instanceof ApiError && err.message ? err.message : 'Something went wrong.', 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <div className="portlet light">
      <h4>
        <strong>Projects</strong> notifications
      </h4>
      <hr />
      <p>
        Notifications are{' '}
        <strong>{q.data.active ? 'on' : 'off'}</strong>
        {q.data.exists ? '' : ' (this service provider has no setting written yet)'}.
      </p>
      {/* Intrerupatorul (`create`) si "Refresh cron"
          (`rebuild_project_notifications_configuration`) - mutate pe 12 septembrie 2026. */}
      <p>
        <label style={{ fontWeight: 400 }}>
          <input type="checkbox" className="icheck" name="status" checked={Boolean(q.data.active)} disabled={busy} onChange={(e) => flip(e.target.checked)} />{' '}
          Enable project notifications
        </label>
      </p>
      <button type="button" className="btn btn-sm btn-default rebuild-configuration" disabled={busy} onClick={rebuild}>
        <i className="fas fa-sync" aria-hidden="true"></i> Refresh cron
      </button>
    </div>
  )
}

function OrganisationsPanel() {
  const client = useQueryClient()
  /* 0 = inchis; -1 = organizatie noua; >0 = editare. */
  const [editing, setEditing] = useState(0)
  const [adding, setAdding] = useState(0)
  const [state, setState] = useTableState(ORGANISATIONS_TABLE, {
    start: 0,
    length: 25,
    /* `order: [[0, 'desc']]` din easeetask.js - pe ce si cum porneste lista veche. */
    order: { column: 0, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => organisationColumns(), [])
  const table = useQuery({
    ...organisationTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORGANISATION_ORDER_NAMES[state.order.column] ?? 'name',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const refresh = () => void client.invalidateQueries({ queryKey: ['settings', 'easeetask'] })

  /* Butoanele stau in HTML-ul celulei; clicul se prinde pe invelis. */
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('.edit-organisation, .add-members, .delete-organisation')
    if (!button) return
    const id = Number(button.dataset.id)
    if (id < 1) return
    if (button.classList.contains('edit-organisation')) setEditing(id)
    else if (button.classList.contains('add-members')) setAdding(id)
    else if (window.confirm('Delete this organisation?')) {
      void removeOrganisation(id)
        .then(() => {
          toast('Organisation deleted.', 'Success')
          refresh()
        })
        .catch((err: unknown) => toast(err instanceof ApiError && err.message ? err.message : 'Something went wrong.', 'Error', 'error'))
    }
  }

  return (
    <div onClick={onClick}>
      <button type="button" className="pull-right btn btn-success btn-sm create-organisation" onClick={() => setEditing(-1)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create organisation
      </button>
      <h4>
        <strong>Organisations</strong>
      </h4>
      <hr />
      {editing !== 0 ? (
        <OrganisationModal
          id={editing > 0 ? editing : 0}
          onClose={() => setEditing(0)}
          onSaved={() => {
            setEditing(0)
            refresh()
          }}
        />
      ) : null}
      {adding > 0 ? (
        <AddMemberModal
          organisation={adding}
          onClose={() => setAdding(0)}
          onSaved={() => {
            setAdding(0)
            refresh()
          }}
        />
      ) : null}
      <DataTable
        id={ORGANISATIONS_TABLE}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.id)}`)}
        emptyHint="An organisation groups the people whose working hours are planned together."
      />
    </div>
  )
}

function BusinessHoursPanel({ organisation, onPick, organisations }: {
  organisation: number
  onPick(next: number): void
  organisations: { id: number; name: string; main: number }[]
}) {
  const [state, setState] = useTableState(HOURS_TABLE, {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => businessHoursColumns(), [])
  const table = useQuery({
    ...businessHoursTableQuery(organisation, {
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: HOURS_ORDER_NAMES[state.order.column] ?? 'userName',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  /* `updateUserBusinessHours/{user}` cu `columnName`/`value`, la fiecare ora
     schimbata in celula - ca `editable` din easeetask.js. */
  const onHourChange = (event: FormEvent<HTMLDivElement>) => {
    const box = event.target as HTMLInputElement
    if (!(box instanceof HTMLInputElement) || !box.classList.contains('business-hour')) return
    const user = Number(box.dataset.user)
    const column = box.dataset.column === 'End' ? 'End' : 'Start'
    if (user < 1 || box.value === '') return
    void saveBusinessHours(user, column, box.value)
      .then(() => toast('Business hours updated.', 'Success'))
      .catch((err: unknown) => {
        toast(err instanceof ApiError && err.message ? err.message : 'Something went wrong.', 'Error', 'error')
        void table.refetch()
      })
  }

  return (
    <div onChange={onHourChange}>
      <h4>
        <strong>Organisations</strong> business hours
      </h4>
      <hr />
      {/*
        `#organisationFilter` din `organisations/index.php`: sta deasupra
        tabelului, ca in fragmentul vechi. Acolo DataTables il muta in slotul
        `.organisationFilters` din `dom`; aici e scris direct la locul lui.
      */}
      <div className="form-group" style={{ maxWidth: 320 }}>
        <label htmlFor="organisationFilter">Organisation</label>
        <select
          id="organisationFilter"
          aria-label="Organisation"
          className="form-control"
          value={organisation}
          onChange={(e) => {
            onPick(Number(e.target.value))
            setState({ start: 0 })
          }}
        >
          {organisations.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>
      <DataTable
        id={HOURS_TABLE}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.user_id)}`)}
        emptyHint="Pick an organisation to see the working hours of its people."
      />
    </div>
  )
}

export function EaseetaskSettingsPage() {
  const shell = useQuery(easeetaskShellQuery)
  const [tab, setTab] = useState<EaseetaskTab['key'] | null>(null)
  const [organisation, setOrganisation] = useState(0)

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const tabs = shell.data.tabs.filter((t) => t.shown)
  /* Macheta veche apasa singura primul rand din bara la deschidere. */
  const active = tab ?? tabs[0]?.key ?? null
  /* Organizatia principala e cea preselectata, ca `option[main == 1]`. */
  const picked = organisation || shell.data.organisations.find((o) => o.main === 1)?.id
    || shell.data.organisations[0]?.id || 0

  /*
   * Fara `row`/`col-md-*`, ca la celelalte meniuri de setari ale unui modul.
   *
   * Coloanele nu faceau nimic aici, si una din ele strica: `.od-dark-sidebar` e
   * `position: fixed`, deci `col-md-3` nu-i da nicio latime, iar pe partea
   * cealalta `.page-content-wrapper { width: auto }` din `app.css` bate
   * `col-md-9 { width: 75% }` (aceeasi specificitate, scrisa mai tarziu). Ce
   * ramanea era un `float: left` cu latime automata, adica o coloana STRANSA
   * pe cuvintele ei: masurat in Edge la 1440, continutul avea 62 de pixeli
   * latime. Acum e ca peste tot - meniul la 0-270, continutul de la 290 la
   * 1420.
   */
  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav nav nav-pills nav-stacked">
          {/* `menu-header`, ca in celelalte opt meniuri de modul. Era
              `nav-header`, pe care nu-l stilizeaza nimeni: in portal clasa aia
              apare numai in sub-rail-urile de alarme si intotdeauna cu un
              `font-weight: bold` scris in markup langa ea. Aici ramanea un
              cuvant mic si lipit de marginea de sus. `menu-header` il face
              capul de lista pe care il are orice alt meniu de setari
              (`settings_console.css`, sectiunea 10). */}
          <li className="menu-header">Projects</li>
          {tabs.map((t) => (
            <li key={t.key} className={`menu-tab${t.key === active ? ' active' : ''}`}>
              <a
                href={`/${shell.data.moduleUrl}/${t.key}`}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(t.key)
                }}
              >
                {t.label}
              </a>
            </li>
          ))}
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {active === 'project_notifications' ? <ProjectNotificationsPanel /> : null}
        {active === 'organisations' ? <OrganisationsPanel /> : null}
        {active === 'user_organisations' ? (
          <BusinessHoursPanel
            organisation={picked}
            onPick={setOrganisation}
            organisations={shell.data.organisations}
          />
        ) : null}
        {active === null ? <Forbidden /> : null}
      </div>
    </>
  )
}
